import { ClientProfileItem } from './client-profile.model';

export type ClientProfileValueDisplayKind =
  | 'text'
  | 'date'
  | 'dob'
  | 'currency'
  | 'phone';

/** Keys that suggest a monetary amount (API does not send explicit types). */
const CURRENCY_KEY_INCLUDE =
  /(salary|premium|wage|_amount$|annual_pay|hourly_pay|compensation|\brate\b)/i;

/** Keys that look like money but are identifiers, labels, or non-currency fields. */
const CURRENCY_KEY_EXCLUDE =
  /(_code|_id|eligible|frequency|status|_plan$|coverage|location|company|email|phone|address|guid|name|internal|type$|title)/i;

/** Keys that suggest a calendar date when the value is parseable. */
const DATE_KEY_HINT = /(_date$|_at$|date_of|(^|_)dob($|_)|birth)/i;

/** Keys that suggest a phone number (API does not send explicit types). */
const PHONE_KEY_HINT =
  /phone|mobile|cell|fax|telephone|tel_number|tel_no|sms/i;

function looksLikeIsoDateString(value: string): boolean {
  const s = value.trim();
  if (!s) {
    return false;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return true;
  }
  return /^\d{4}-\d{2}-\d{2}T/.test(s);
}

function parseableAsDate(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return !Number.isNaN(d.getTime());
  }
  const t = Date.parse(value);
  return !Number.isNaN(t);
}

export function toFiniteNumber(
  value: string | number | null | undefined
): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const n = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function isLikelyCurrencyKey(key: string | undefined): boolean {
  if (!key) {
    return false;
  }
  if (CURRENCY_KEY_EXCLUDE.test(key)) {
    return false;
  }
  return CURRENCY_KEY_INCLUDE.test(key);
}

function isLikelyDateKey(key: string | undefined): boolean {
  if (!key) {
    return false;
  }
  return DATE_KEY_HINT.test(key);
}

function isLikelyPhoneKey(key: string | undefined): boolean {
  if (!key) {
    return false;
  }
  return PHONE_KEY_HINT.test(key);
}

function digitCount(value: string | number): number {
  return String(value).replace(/\D/g, '').length;
}

function looksLikePhoneValue(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  const n = digitCount(value);
  return n >= 7 && n <= 15;
}

/**
 * Formats a phone-like value for display (US-style when 10 or 11 digits starting with 1).
 * Returns null when the value does not look like a dialable number.
 */
export function formatClientProfilePhoneDisplay(
  value: string | number
): string | null {
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    const d = digits.slice(1);
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  if (digits.length === 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  if (digits.length >= 8 && digits.length <= 15) {
    return `+${digits}`;
  }
  return null;
}

/**
 * Chooses how to render a profile item. Uses optional `key` heuristics and value shape;
 * the API does not declare field types.
 */
export function inferClientProfileValueDisplayKind(
  item: ClientProfileItem,
  value: string | number | null | undefined
): ClientProfileValueDisplayKind {
  const dobKey = (item.key ?? '').replace(/[^a-z]/gi, '').toLowerCase();
  const dobLabel = item.label.replace(/[^a-z]/gi, '').toLowerCase();
  if ([dobKey, dobLabel].some((key) => ['dob', 'dateofbirth', 'birthdate'].includes(key))) {
    return 'dob';
  }

  if (value === null || value === undefined || value === '') {
    return 'text';
  }

  if (typeof value === 'string' && looksLikeIsoDateString(value)) {
    return 'date';
  }

  if (isLikelyDateKey(item.key) && parseableAsDate(value)) {
    return 'date';
  }

  if (isLikelyPhoneKey(item.key) && looksLikePhoneValue(value)) {
    return 'phone';
  }

  if (isLikelyCurrencyKey(item.key) && toFiniteNumber(value) !== null) {
    return 'currency';
  }

  return 'text';
}
